import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import { showNotification } from "@redotech/redo-web/alert";
import { ClientError } from "@redotech/rpc/errors";
import { sleep } from "@redotech/util/schedule";
import { memo, useEffect, useState } from "react";
import { BehaviorSubject } from "rxjs";
import { ProgressNotification } from "./progress-notification";

type AutoMergeProgress = Awaited<
  ReturnType<RedoOutboundLabelsRpcClient["getAutoMergeProgress"]>
>;

interface AutoMergeStatusDisplayProps {
  progress: BehaviorSubject<AutoMergeProgress>;
  dismiss: () => void;
  teamId: string;
}

export async function showAutoMergeNotification(
  teamId: string,
  client: RedoOutboundLabelsRpcClient,
  start: Promise<{ workflowId: string | null }>,
  total: number,
): Promise<void> {
  const progress = new BehaviorSubject<AutoMergeProgress>({
    status: "running",
    processed: 0,
    total,
  });
  showNotification({
    dataTestId: "auto-merge-status",
    hideCloseX: true,
    noPadding: true,
    children: (dismiss) => (
      <AutoMergeStatusDisplay
        dismiss={dismiss}
        progress={progress}
        teamId={teamId}
      />
    ),
  });

  try {
    const { workflowId } = await start;
    if (!workflowId) {
      progress.next({ status: "done", processed: total, total });
      return;
    }

    while (true) {
      let result: AutoMergeProgress;
      try {
        result = await client.getAutoMergeProgress({ workflowId });
      } catch (error) {
        if (error instanceof ClientError && error.statusCode < 500) {
          throw error;
        }
        console.error("Failed to poll automerge progress:", error);
        await sleep(Temporal.Duration.from({ seconds: 5 }));
        continue;
      }
      if (result.total === 0 && progress.value.total > 0) {
        result = { ...progress.value, status: result.status };
      }
      if (result.status === "done") {
        progress.next({ ...result, processed: result.total });
        return;
      }
      progress.next(result);
      if (result.status === "failed") {
        throw new Error("Automerge workflow failed");
      }
      await sleep(Temporal.Duration.from({ seconds: 5 }));
    }
  } catch (error) {
    progress.next({ ...progress.value, status: "failed" });
    throw error;
  } finally {
    progress.complete();
  }
}

const AutoMergeStatusDisplay = memo(function AutoMergeStatusDisplay({
  progress,
  dismiss,
  teamId,
}: AutoMergeStatusDisplayProps) {
  const [value, setValue] = useState(progress.value);
  const [ellipsis, setEllipsis] = useState(".");

  useEffect(() => {
    const subscription = progress.subscribe(setValue);
    return () => subscription.unsubscribe();
  }, [progress]);

  useEffect(() => {
    if (value.status !== "running") {
      return;
    }
    const interval = setInterval(() => {
      setEllipsis((current) => (current.length >= 3 ? "." : current + "."));
    }, 1000);
    return () => clearInterval(interval);
  }, [value.status]);

  const percentage =
    value.status === "done"
      ? 100
      : value.total > 0
        ? Math.min(99, Math.round((value.processed / value.total) * 100))
        : 0;

  return (
    <ProgressNotification
      dismiss={dismiss}
      percentage={percentage}
      showDashboardLink={false}
      subtext={`${value.processed} of ${value.total} fulfillments processed`}
      teamId={teamId}
      title={
        value.status === "done"
          ? "Automerge applied"
          : value.status === "failed"
            ? "Failed to apply automerge"
            : `Applying automerge${ellipsis}`
      }
    />
  );
});
